import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ConnectButton, useConnectModal, useAccountModal } from '@rainbow-me/rainbowkit';
import { useAccount, useBalance } from 'wagmi';
import { formatEther } from 'viem';
import { useTheme } from '../context/ThemeContext';
import { useHomeViewMode } from '../context/HomeViewModeContext';
import { useDesignMode } from '../context/DesignModeContext';
import {
  getCardStyle,
  textStyle,
  chamferClipPath,
  themeCornerIsChamfer,
  getButtonSurfaceStyle,
  isDarkDesignMode,
  type DesignMode,
} from '../config/theme';
import { useEthPrice } from '../hooks/useEthPrice';
import { useIsMobile } from '../hooks/useMediaQuery';
import { formatHeaderUsd2 } from '../lib/headerUsdFormat';
import { ADMIN_WALLET_LOWER } from '../config/adminWallet';
import { LegalDisclaimerModal } from './LegalDisclaimerModal';
import { FooterActions } from './FooterActions';
import { useFarcasterMiniApp } from './FarcasterMiniAppBootstrap';

/** Rectangular rail shadow — round corner mode only (`box-shadow` + chamfer clipped fill = wedge gaps). */
const NAV_SHELL_HARD_SHADOW =
  '0 1px 0 rgba(255, 255, 255, 0.19) inset, 0 2px 8px rgba(0, 0, 0, 0.07), 0 7px 22px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.035)';

const NAV_SHELL_HARD_SHADOW_DARK =
  '0 1px 0 rgba(255, 255, 255, 0.02) inset, 0 4px 14px rgba(0, 0, 0, 0.19), 0 1px 4px rgba(0, 0, 0, 0.14)';

const NAV_UNIFIED_BOX_SHADOW =
  '0 1px 2px rgba(0, 0, 0, 0.055), 0 3px 8px rgba(0, 0, 0, 0.04), 0 1px 1px rgba(0, 0, 0, 0.035)';

/** Outer `filter` on a wrapper whose child carries `clip-path` — shadow follows silhouette (no cut-off corners vs box-shadow rect). */
const NAV_UNIFIED_DROP_LIFT =
  'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.065)) drop-shadow(0 5px 9px rgba(0, 0, 0, 0.04))';

function navChamferRailFilter(theme: Parameters<typeof getCardStyle>[0], mode: DesignMode): string {
  /** Ring as spread blur so one bad segment can’t invalidate the whole `filter` in WebKit/Chromium */
  const ring = `drop-shadow(0 0 0.85px rgba(0, 0, 0, 0.16))`;
  const diffuse = isDarkDesignMode(mode)
    ? `drop-shadow(0 0 0 1px ${theme.border}) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.275)) drop-shadow(0 10px 24px rgba(0, 0, 0, 0.225)) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.2))`
    : `drop-shadow(0 0 0 1px ${theme.border}) drop-shadow(0 2px 10px rgba(0, 0, 0, 0.08)) drop-shadow(0 10px 28px rgba(0, 0, 0, 0.06)) drop-shadow(0 4px 6px rgba(0, 0, 0, 0.05))`;
  return `${ring} ${diffuse}`.trim();
}

function navChamferRailFilterCss(theme: Parameters<typeof getCardStyle>[0], mode: DesignMode): Pick<React.CSSProperties, 'WebkitFilter' | 'filter'> {
  const f = navChamferRailFilter(theme, mode);
  return { WebkitFilter: f, filter: f };
}

function navShellBoxShadow(mode: DesignMode): string {
  return isDarkDesignMode(mode) ? NAV_SHELL_HARD_SHADOW_DARK : NAV_SHELL_HARD_SHADOW;
}

function parseHexRgb(hex: string): { r: number; g: number; b: number } | null {
  let x = hex.replace('#', '').trim();
  if (x.length === 3) x = [...x].map((c) => c + c).join('');
  if (x.length !== 6 || !/^[0-9a-fA-F]+$/.test(x)) return null;
  return {
    r: parseInt(x.slice(0, 2), 16),
    g: parseInt(x.slice(2, 4), 16),
    b: parseInt(x.slice(4, 6), 16),
  };
}

function mixHexColor(a: string, b: string, ratio = 0.5): string {
  const ca = parseHexRgb(a);
  const cb = parseHexRgb(b);
  if (!ca || !cb) return b;
  const ch = (key: 'r' | 'g' | 'b') => Math.round(ca[key] + (cb[key] - ca[key]) * ratio);
  return `#${[ch('r'), ch('g'), ch('b')].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/** Header rail only — halfway between page bg and post/card bg; does not touch `theme.navBg`. */
function navHeaderRailBackground(theme: Parameters<typeof getCardStyle>[0]): string {
  if (typeof theme.navBg === 'string' && theme.navBg.length > 0 && !theme.navBg.includes('gradient')) {
    return theme.navBg;
  }
  const pageBg =
    typeof theme.bg === 'string' && !theme.bg.includes('gradient') ? theme.bg : (theme.bgBase ?? '#faf5ff');
  return mixHexColor(pageBg, theme.bgCard ?? '#ffffff', 0.5);
}

function navRailPadding(theme: Parameters<typeof getCardStyle>[0]): string {
  return theme.navStyle === 'compact'
    ? '0.25rem 0.75rem'
    : theme.navStyle === 'floating'
      ? '0.5rem 1rem'
      : '0.375rem 1rem';
}

/** Chamfer tiles: elevation from `NavChamferShadowWrap` filter only — inner keeps `clip-path` without conflicting rect shadow. */
function navChromeButtonElevation(theme: Parameters<typeof getCardStyle>[0]): React.CSSProperties {
  if (themeCornerIsChamfer(theme)) {
    return { filter: 'none', boxShadow: 'none' };
  }
  return { filter: 'none', boxShadow: NAV_UNIFIED_BOX_SHADOW };
}

/**
 * Chamfer rail fill + elevation.
 * `clip-path` clips **everything** painted on that element (including its own `filter`), so **never**
 * combine `filter: drop-shadow()` on the same node as `clip-path` — shadow stays invisible.
 * Pattern: `filter` on parent, `clip-path` + opaque fill on child (CSS-Tricks / common workaround).
 * Glass: same opaque child for silhouette + separate overlay for blur (backdrop on filtered node is flaky).
 */
function NavChamferRailBackdrop({
  theme,
  designMode,
  railBg,
}: {
  theme: Parameters<typeof getCardStyle>[0];
  designMode: DesignMode;
  railBg: string;
}) {
  const cp = chamferClipPath(theme.chamferCut ?? '7px');
  const opaqueFill = railBg;
  const glass = theme.cardStyle === 'glass';

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'visible',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'visible',
          ...navChamferRailFilterCss(theme, designMode),
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            clipPath: cp,
            WebkitClipPath: cp,
            backgroundColor: opaqueFill,
            border: 'none',
            boxShadow: 'none',
          }}
        />
      </div>
      {glass && (
        <div
          style={
            {
              position: 'absolute',
              inset: 0,
              clipPath: cp,
              WebkitClipPath: cp,
              pointerEvents: 'none',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              backgroundColor: isDarkDesignMode(designMode) ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.35)',
              zIndex: 1,
            } as React.CSSProperties
          }
        />
      )}
    </div>
  );
}

function NavChamferShadowWrap({
  theme,
  children,
}: {
  theme: Parameters<typeof getCardStyle>[0];
  children: React.ReactNode;
}) {
  if (!themeCornerIsChamfer(theme)) return <>{children}</>;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        lineHeight: 0,
        filter: NAV_UNIFIED_DROP_LIFT,
      }}
    >
      {children}
    </span>
  );
}

/** Rounded-corner nav chrome fill/border (`boxShadow` applied on outer shell always). */
function navRoundRailSurface(
  theme: Parameters<typeof getCardStyle>[0],
  headerRailBg: string,
): React.CSSProperties {
  const navRadius =
    theme.navStyle === 'floating' ? '1.5rem' : theme.cardRadius === '0' ? '0' : '1rem';
  const gs = getCardStyle(theme, { skipAccent: true }) as React.CSSProperties;
  return {
    ...gs,
    backgroundColor: headerRailBg,
    borderRadius: navRadius,
    filter: 'none',
  };
}

const btnStyle = {
  width: '2.5rem',
  height: '2.5rem',
  display: 'flex',
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  borderRadius: '0.375rem',
  cursor: 'pointer',
  textDecoration: 'none' as const,
  boxSizing: 'border-box' as const,
  backgroundColor: 'white',
};

const mobileBtnStyle = {
  ...btnStyle,
  width: '1.95rem',
  height: '1.95rem',
};

function navDropdownRoundPanel(theme: Parameters<typeof getCardStyle>[0]): React.CSSProperties {
  return {
    borderRadius: theme.cardRadius ?? '0.5rem',
    boxShadow: '0 12px 18px -3px rgba(0, 0, 0, 0.3), 0 6px 12px -6px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${theme.border}`,
    filter: 'none',
  };
}

function navIconTileStyle(theme: Parameters<typeof getCardStyle>[0]): React.CSSProperties {
  if (themeCornerIsChamfer(theme)) {
    const cp = chamferClipPath(theme.chamferCutButton ?? '5px');
    return { borderRadius: 0, clipPath: cp, WebkitClipPath: cp };
  }
  return { borderRadius: '0.375rem' };
}

function navRailMobileControlShape(theme: Parameters<typeof getCardStyle>[0]): React.CSSProperties {
  if (themeCornerIsChamfer(theme)) {
    const cp = chamferClipPath(theme.chamferCutButton ?? '5px');
    return { borderRadius: 0, clipPath: cp, WebkitClipPath: cp };
  }
  return { borderRadius: '0.375rem' };
}

/** Tile fill: almost `surfaceBase`, slight wash of `accent` — much lighter than border/icon */
function navIconTileSoftFill(accent: string, surfaceBase: string, accentFraction = 0.13): string {
  const A = parseHexRgb(accent);
  const B = parseHexRgb(surfaceBase);
  if (!A || !B) return surfaceBase;
  const w = accentFraction;
  const mix = (ac: number, bc: number) => Math.round(ac * w + bc * (1 - w));
  const r = mix(A.r, B.r);
  const gv = mix(A.g, B.g);
  const b = mix(A.b, B.b);
  const h = (n: number) => n.toString(16).padStart(2, '0');
  return `#${h(r)}${h(gv)}${h(b)}`;
}

const navLinks = (
  isAdmin: boolean,
  theme: { yellow?: string; teal?: string; purple?: string; pink?: string; amber?: string; navTextColor?: string },
  isConnected: boolean,
) => [
  ...(isConnected ? [] : [{ to: '/explore', label: 'Explore', color: theme.pink ?? '#ec4899' }]),
  { to: '/', label: 'Markets', color: theme.yellow ?? '#eab308' },
  { to: '/profile', label: 'Profile', color: theme.teal ?? '#14b8a6' },
  ...(isAdmin ? [{ to: '/admin', label: 'Admin', color: theme.amber ?? '#f59e0b' }] : []),
  { to: '/create', label: 'Launch', color: theme.purple ?? '#8b5cf6' },
];

/** Filled geometric icons for desktop nav tiles — keyed by route. */
function navDesktopIcon(to: string): React.ReactNode {
  switch (to) {
    case '/':
      return (
        <svg width="1.25rem" height="1.25rem" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <rect x="4" y="4" width="16" height="16" />
        </svg>
      );
    case '/explore':
      return (
        <svg width="1.25rem" height="1.25rem" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <rect x="4" y="14" width="4" height="6" rx="0.5" />
          <rect x="10" y="9" width="4" height="11" rx="0.5" />
          <rect x="16" y="4" width="4" height="16" rx="0.5" />
        </svg>
      );
    case '/profile':
      return (
        <svg width="1.25rem" height="1.25rem" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2L2 22h20L12 2z" />
        </svg>
      );
    case '/admin':
      return (
        <svg width="1.25rem" height="1.25rem" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
        </svg>
      );
    case '/create':
      return (
        <svg width="1.25rem" height="1.25rem" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <rect x="10.7" y="4" width="2.6" height="16" />
          <rect x="4" y="10.7" width="16" height="2.6" />
        </svg>
      );
    default:
      return null;
  }
}

type NavbarMobileMenuBodyProps = {
  theme: Parameters<typeof getCardStyle>[0];
  isAdmin: boolean;
  isConnected: boolean;
  designMode: DesignMode;
  setDesignMode: (m: DesignMode) => void;
  hoverMode: number | null;
  setHoverMode: React.Dispatch<React.SetStateAction<number | null>>;
  fetchedAt: number | null | undefined;
  ethPriceError: unknown;
  setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLegalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

/** Shared mobile dropdown body (fills chamfer-clipped shell or rounded panel wrapper). */
function NavbarMobileMenuBody({
  theme,
  isAdmin,
  isConnected,
  designMode,
  setDesignMode,
  hoverMode,
  setHoverMode,
  fetchedAt,
  ethPriceError,
  setMenuOpen,
  setLegalOpen,
}: NavbarMobileMenuBodyProps) {
  return (
    <>
      {navLinks(isAdmin, theme, isConnected).map((link) => (
        <Link
          key={link.to}
          to={link.to}
          onClick={() => setMenuOpen(false)}
          style={{
            display: 'block',
            padding: '0.6rem 0.75rem',
            color: link.color ?? theme.textPrimary,
            textDecoration: 'none',
            fontSize: '1.0625rem',
            lineHeight: 1.35,
            fontWeight: 700,
            ...getButtonSurfaceStyle(theme),
          }}
        >
          {link.label}
        </Link>
      ))}
      <FooterActions
        theme={theme}
        designMode={designMode}
        setDesignMode={setDesignMode}
        hoverMode={hoverMode}
        setHoverMode={setHoverMode}
        fetchedAt={fetchedAt}
        priceError={ethPriceError}
        onLegalClick={() => setLegalOpen(true)}
        onItemActivate={() => setMenuOpen(false)}
        layout="stack"
      />
    </>
  );
}

const Navbar: React.FC = () => {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [hoverMode, setHoverMode] = useState<number | null>(null);
  const { address, isConnected } = useAccount();
  const { isMiniApp } = useFarcasterMiniApp();
  const { data: balance } = useBalance({ address });
  const { ethUsdPrice, fetchedAt, error: ethPriceError } = useEthPrice();
  const { designMode, setDesignMode } = useDesignMode();
  const theme = useTheme();
  const balanceUsdNum =
    balance && ethUsdPrice != null ? Number(formatEther(balance.value)) * ethUsdPrice : 0;
  const location = useLocation();
  const { viewMode, toggleViewMode } = useHomeViewMode();
  const isAdmin = address?.toLowerCase() === ADMIN_WALLET_LOWER;
  const navTileSurfaceRaw = theme.navBg ? (theme.navTextColor ?? theme.bgCard) : theme.bgCard;
  const navTileMixBase =
    typeof navTileSurfaceRaw === 'string' && !navTileSurfaceRaw.includes('gradient')
      ? navTileSurfaceRaw
      : String(theme.bgCard ?? '#ffffff');
  const isHome = location.pathname === '/';
  const isExplore = location.pathname === '/explore';
  const showCardViewToggle = isHome || isExplore;
  const navRailPad = navRailPadding(theme);
  const headerRailBg = navHeaderRailBackground(theme);
  /** TAKNI contrasts the header; XYZ keeps brand purple at the current watermark opacity. */
  const headerIsDark = isDarkDesignMode(designMode);
  const takniFill = headerIsDark ? '#ffffff' : '#000000';
  const XYZ_PURPLE = parseHexRgb('#8b5cf6');
  const xyzFill = XYZ_PURPLE
    ? `rgba(${XYZ_PURPLE.r}, ${XYZ_PURPLE.g}, ${XYZ_PURPLE.b}, 0.288)`
    : 'rgba(139, 92, 246, 0.288)';
  const chamferRail = themeCornerIsChamfer(theme);
  const dropdownChamferClip = chamferRail ? chamferClipPath(theme.chamferCut ?? '7px') : undefined;
  const desktopConnectClip = chamferRail ? chamferClipPath(theme.chamferCutButton ?? '5px') : undefined;
  const { openConnectModal } = useConnectModal();
  const { openAccountModal } = useAccountModal();

  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!legalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLegalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [legalOpen]);

  return (
    <nav
      style={{
        backgroundColor: 'transparent',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        padding: theme.navStyle === 'floating' ? '1rem 1.5rem' : theme.navStyle === 'compact' ? '0.5rem 1rem' : '1rem',
        overflow: 'visible',
      }}
    >
      <div
        style={{
          position: 'relative',
          maxWidth: '1200px',
          margin: '0 auto',
          overflow: 'visible',
          ...(chamferRail
            ? {}
            : {
                padding: navRailPad,
                ...navRoundRailSurface(theme, headerRailBg),
              }),
          boxShadow: chamferRail ? 'none' : navShellBoxShadow(designMode),
        }}
      >
        {chamferRail && (
          <NavChamferRailBackdrop theme={theme} designMode={designMode} railBg={headerRailBg} />
        )}
        <div
          style={{
            position: chamferRail ? 'relative' : undefined,
            zIndex: chamferRail ? 1 : undefined,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: chamferRail ? '100%' : undefined,
            boxSizing: 'border-box',
            minWidth: 0,
            padding: chamferRail ? navRailPad : undefined,
            overflow: 'visible',
          }}
        >
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '0.5rem',
            minWidth: 0,
          }}
        >
          {isMobile ? (
            <>
              <NavChamferShadowWrap theme={theme}>
                <button
                  onClick={() => setMenuOpen((o) => !o)}
                  style={{
                    ...mobileBtnStyle,
                    ...navIconTileStyle(theme),
                    ...navChromeButtonElevation(theme),
                    border: 'none',
                    color: theme.navTextColor ?? theme.textMuted ?? '#6b7280',
                    background: 'transparent',
                  }}
                  aria-label="Menu"
                >
                  <svg width="1.68rem" height="1.68rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </NavChamferShadowWrap>
              {menuOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 0.25rem)', left: 0, right: 0, zIndex: 100 }}>
                  {chamferRail ? (
                    <NavChamferShadowWrap theme={theme}>
                      <div
                        style={{
                          clipPath: dropdownChamferClip,
                          WebkitClipPath: dropdownChamferClip,
                          overflow: 'hidden',
                          padding: '0.5rem',
                          backgroundColor: theme.bgCard,
                          border: `1px solid ${theme.border}`,
                          boxShadow: 'none',
                          filter: 'none',
                          ...(theme.cardStyle === 'glass'
                            ? ({
                                backdropFilter: 'blur(16px)',
                                WebkitBackdropFilter: 'blur(16px)',
                              } as React.CSSProperties)
                            : {}),
                        }}
                      >
                        <NavbarMobileMenuBody
                          theme={theme}
                          isAdmin={isAdmin}
                          isConnected={isConnected}
                          designMode={designMode}
                          setDesignMode={setDesignMode}
                          hoverMode={hoverMode}
                          setHoverMode={setHoverMode}
                          fetchedAt={fetchedAt}
                          ethPriceError={ethPriceError}
                          setMenuOpen={setMenuOpen}
                          setLegalOpen={setLegalOpen}
                        />
                      </div>
                    </NavChamferShadowWrap>
                  ) : (
                    <div
                      style={{
                        padding: '0.5rem',
                        backgroundColor: theme.bgCard,
                        ...navDropdownRoundPanel(theme),
                      }}
                    >
                      <NavbarMobileMenuBody
                        theme={theme}
                        isAdmin={isAdmin}
                        isConnected={isConnected}
                        designMode={designMode}
                        setDesignMode={setDesignMode}
                        hoverMode={hoverMode}
                        setHoverMode={setHoverMode}
                        fetchedAt={fetchedAt}
                        ethPriceError={ethPriceError}
                        setMenuOpen={setMenuOpen}
                        setLegalOpen={setLegalOpen}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {navLinks(isAdmin, theme, isConnected).map((link) => (
                <NavChamferShadowWrap key={link.to} theme={theme}>
                  <Link
                    to={link.to}
                    title={link.label}
                    style={{
                      ...btnStyle,
                      ...navIconTileStyle(theme),
                      ...navChromeButtonElevation(theme),
                      backgroundColor: navIconTileSoftFill(link.color, navTileMixBase),
                      border: `2px solid ${link.color}`,
                      color: link.color,
                    }}
                  >
                    {navDesktopIcon(link.to)}
                  </Link>
                </NavChamferShadowWrap>
              ))}

              {showCardViewToggle && (
                <>
                  <div
                    style={{
                      width: '2px',
                      height: '1.58rem',
                      backgroundColor: theme.navTextColor ? 'rgba(255,255,255,0.5)' : '#d1d5db',
                      flexShrink: 0,
                      margin: '0 0.15rem',
                      boxShadow: chamferRail ? 'none' : NAV_UNIFIED_BOX_SHADOW,
                    }}
                    aria-hidden
                  />

                  <NavChamferShadowWrap theme={theme}>
                    <button
                    type="button"
                    onClick={toggleViewMode}
                    title={viewMode === 'grid' ? 'List view (1 per row)' : 'Grid view (2-3 per row)'}
                    style={{
                      ...btnStyle,
                      ...navIconTileStyle(theme),
                      ...navChromeButtonElevation(theme),
                      width: viewMode === 'grid' ? '1.39rem' : '1.98rem',
                      height: viewMode === 'grid' ? '1.39rem' : '1.98rem',
                      backgroundColor: navIconTileSoftFill(
                        theme.navTextColor ?? theme.textMuted ?? '#6b7280',
                        navTileMixBase,
                        0.09,
                      ),
                      border: 'none',
                      color: theme.navTextColor ?? theme.textMuted ?? '#6b7280',
                      padding: 0,
                    }}
                    aria-label={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
                  >
                    {viewMode === 'grid' ? (
                      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block' }}>
                        <rect x="0" y="0" width="24" height="24" rx="1" />
                      </svg>
                    ) : (
                      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block' }}>
                        <rect x="0" y="0" width="7.04" height="7.04" /><rect x="8.48" y="0" width="7.04" height="7.04" /><rect x="16.96" y="0" width="7.04" height="7.04" />
                        <rect x="0" y="8.48" width="7.04" height="7.04" /><rect x="8.48" y="8.48" width="7.04" height="7.04" /><rect x="16.96" y="8.48" width="7.04" height="7.04" />
                        <rect x="0" y="16.96" width="7.04" height="7.04" /><rect x="8.48" y="16.96" width="7.04" height="7.04" /><rect x="16.96" y="16.96" width="7.04" height="7.04" />
                      </svg>
                    )}
                    </button>
                  </NavChamferShadowWrap>

                </>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Link
            to="/"
            aria-label="Takni"
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              color: takniFill,
              lineHeight: 1,
            }}
          >
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: `calc(${theme.headingSize} * 1.656)`,
                fontWeight: 900,
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
                color: xyzFill,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                zIndex: 0,
              }}
            >
              XYZ
            </span>
            <span
              style={{
                position: 'relative',
                zIndex: 1,
                fontSize: theme.headingSize,
                fontWeight: theme.headingWeight,
                letterSpacing: '-0.025em',
                textTransform: 'uppercase',
              }}
            >
              Takni
            </span>
          </Link>
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '1.25rem',
            minWidth: 0,
            overflow: 'visible',
          }}
        >
          {!isMobile && isConnected && address && (
            <div
              aria-label="Wallet balance"
              style={{
                flexShrink: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '0.05rem',
                ...textStyle('xs'),
                fontWeight: 600,
                letterSpacing: '0.06em',
                color: theme.navTextColor ?? theme.textMuted ?? '#6b7280',
                fontVariantNumeric: 'tabular-nums' as const,
                textAlign: 'right',
                lineHeight: 1.2,
              }}
            >
              <span>
                <span style={{ opacity: 0.92 }}>BALANCE</span> {formatHeaderUsd2(balanceUsdNum)}
              </span>
            </div>
          )}
          {isMobile ? (
            isConnected && address ? (
              <NavChamferShadowWrap theme={theme}>
                <button
                  onClick={() => openAccountModal?.()}
                  style={{
                    padding: '0.375rem 0.75rem',
                    ...textStyle('base'),
                    fontWeight: 500,
                    color: theme.navTextColor ?? theme.textPrimary ?? '#374151',
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.navTextColor ?? theme.border}`,
                    ...getButtonSurfaceStyle(theme),
                    ...navRailMobileControlShape(theme),
                    ...navChromeButtonElevation(theme),
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    flexShrink: 0,
                  }}
                >
                  …{address.slice(-5)}
                </button>
              </NavChamferShadowWrap>
            ) : (
              <NavChamferShadowWrap theme={theme}>
                <button
                  onClick={() => openConnectModal?.()}
                  style={{
                    padding: '0.375rem 0.75rem',
                    ...textStyle('base'),
                    fontWeight: 500,
                    backgroundColor: theme.btnPrimaryBg ?? theme.primary,
                    color: theme.btnPrimaryColor ?? 'white',
                    border: 'none',
                    ...getButtonSurfaceStyle(theme),
                    ...navRailMobileControlShape(theme),
                    ...navChromeButtonElevation(theme),
                    cursor: 'pointer',
                  }}
                >
                  Log in
                </button>
              </NavChamferShadowWrap>
            )
          ) : isMiniApp ? (
            <div style={{ flexShrink: 0, fontSize: '0.8125rem', fontWeight: 600, color: theme.textPrimary }}>
              {isConnected && address ? `…${address.slice(-5)}` : 'Connecting…'}
            </div>
          ) : chamferRail ? (
            <NavChamferShadowWrap theme={theme}>
              <div
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  borderRadius: 0,
                  clipPath: desktopConnectClip,
                  WebkitClipPath: desktopConnectClip,
                }}
              >
                <ConnectButton />
              </div>
            </NavChamferShadowWrap>
          ) : (
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', ...navChromeButtonElevation(theme) }}>
              <ConnectButton />
            </div>
          )}
        </div>
        </div>
      </div>
      <LegalDisclaimerModal open={legalOpen} onClose={() => setLegalOpen(false)} theme={theme} />
    </nav>
  );
};

export default Navbar;
